import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../lib/useMediaQuery';
import { useNavigateTransition } from '../lib/navigation';
import { QUIZ_COPY } from '../lib/quizCopy';
import './Home.scss';

// Locales that have shipped their own share-BG variants in `public/img/`.
// `composeShare.ts` falls back to EN for anything else, so the prefetch list
// must only request the locale's BGs that actually exist on disk — otherwise
// we'd burn the prefetch on a 404.
const SHARE_LOCALES_AVAILABLE: ReadonlyArray<string> = ['en'];

function shareLocaleFor(active: string | undefined): string {
  return active && SHARE_LOCALES_AVAILABLE.includes(active) ? active : 'en';
}

const READY_TIMEOUT_MS = 3000;
// Minimum on-screen time for the preloader so it never flashes by on a warm
// cache — gives the homepage a consistent cinematic intro.
const READY_MIN_HOLD_MS = 1500;

// WebKit (Mac Safari + every iOS browser) decodes VP9 in WebM but renders
// the alpha channel as opaque black. Serve the HEVC `.mov` to those clients
// so the logo composites over the BG video correctly. Other browsers get the
// WebM, which Chrome/Firefox/Edge render with alpha.
function prefersHEVCAlpha(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMacSafari =
    /Macintosh/i.test(ua) && /Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua);
  return isIOS || isMacSafari;
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigateTransition();
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const orientation = isPortrait ? 'tall' : 'wide';

  const bgRef = useRef<HTMLVideoElement>(null);
  const logoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const useHEVCAlpha = useMemo(() => prefersHEVCAlpha(), []);
  const [ready, setReady] = useState(false);
  // Split readiness so orientation changes only re-sync the BG. The logo
  // <video> persists across remounts (no `key={orientation}`), so calling
  // `logo.load()` on every orientation flip would rewind the lockup
  // animation back to its first frame.
  const [bgReady, setBgReady] = useState(false);
  const [logoReady, setLogoReady] = useState(false);
  const [startedAt] = useState(() => performance.now());
  const kickedRef = useRef(false);

  // Logo init runs once — the element persists across orientation changes,
  // so re-running `.load()` on flip would rewind the lockup animation.
  // Both video effects use the same listener triplet + explicit `.load()`
  // pattern: iOS Safari frequently won't fire `canplay` for muted/preload
  // videos until `.play()` is called (which we can't do until ready), so we
  // also listen for `loadeddata` (first frame decoded), and call `.load()`
  // because iOS is conservative about preloading muted videos.
  useEffect(() => {
    const logo = logoRef.current;
    if (!logo) return;
    if (logo.readyState >= 2) {
      setLogoReady(true);
      return;
    }
    const onReady = () => setLogoReady(true);
    logo.addEventListener('loadeddata', onReady);
    logo.addEventListener('canplay', onReady);
    logo.addEventListener('error', onReady);
    logo.load();
    return () => {
      logo.removeEventListener('loadeddata', onReady);
      logo.removeEventListener('canplay', onReady);
      logo.removeEventListener('error', onReady);
    };
  }, []);

  // BG re-syncs on orientation change — the element is remounted via
  // `key={orientation}` so the ref points to a new element each run.
  useEffect(() => {
    const bg = bgRef.current;
    if (!bg) return;
    if (bg.readyState >= 2) {
      setBgReady(true);
      return;
    }
    setBgReady(false);
    const onReady = () => setBgReady(true);
    bg.addEventListener('loadeddata', onReady);
    bg.addEventListener('canplay', onReady);
    bg.addEventListener('error', onReady);
    bg.load();
    return () => {
      bg.removeEventListener('loadeddata', onReady);
      bg.removeEventListener('canplay', onReady);
      bg.removeEventListener('error', onReady);
    };
  }, [orientation]);

  // Safety net so a stalled network can never trap the user on the preloader.
  // Single mount-only timer — re-arming this on each orientation flip would
  // extend the safety budget every rotation during the preload window.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (kickedRef.current) return;
      setBgReady(true);
      setLogoReady(true);
    }, READY_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, []);

  // Coordinator: when both videos are ready, hold for the min-hold window,
  // then start playback and flip the preloader off. On subsequent
  // orientation-driven re-readiness (when only the BG re-loaded), just
  // play the BG — the logo is already running (or has finished and is
  // pinned on its last frame, which is the intended state).
  useEffect(() => {
    if (!bgReady || !logoReady) return;
    if (kickedRef.current) {
      bgRef.current?.play().catch(() => {});
      return;
    }
    kickedRef.current = true;
    const remaining = Math.max(0, READY_MIN_HOLD_MS - (performance.now() - startedAt));
    const id = window.setTimeout(() => {
      bgRef.current?.play().catch(() => {});
      logoRef.current?.play().catch(() => {});
      setReady(true);
    }, remaining);
    return () => window.clearTimeout(id);
  }, [bgReady, logoReady, startedAt]);

  // Drive the preloader bar from 0 → 90% over the min hold via a CSS
  // transition, then snap to 100% the moment we flip ready.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const id = requestAnimationFrame(() => {
      bar.style.setProperty('--fill', '90%');
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || !ready) return;
    bar.style.setProperty('--fill', '100%');
  }, [ready]);

  // Warm the cache for downstream assets so the first nav into the flow feels
  // instant. Fires on whichever happens first: videos finish loading (`ready`
  // flips), or the user clicks the CTA to skip ahead. Link tags persist past
  // unmount so an in-flight fetch isn't cancelled when the user navigates.
  // Locale-specific share BGs are gated to the active locale (with `en`
  // fallback) per SHARE_LOCALES_AVAILABLE — see `lib/composeShare.ts` for the
  // matching fallback rule.
  const prefetchedRef = useRef(false);
  const prefetchFlowAssets = useCallback(() => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;
    const shareLocale = shareLocaleFor(i18n.resolvedLanguage);
    const assets = [
      // Interior-page background (every page after `/`).
      '/img/bg.jpg',
      // ID-card artwork — composited under the lockup for /welcome and the
      // share assets, and used directly as the bg behind /debug/card.
      '/img/card.png',
      // Pre-baked share backgrounds (only the active locale's variant; the
      // fallback to `_en` is handled inside composeShare.ts).
      `/img/share_reg_${shareLocale}.jpg`,
      `/img/share_tall_${shareLocale}.jpg`,
      // Photo-step icons.
      '/img/camera.svg',
      '/img/upload.svg',
      // Loading-screen fact-card icon.
      '/img/bulb.svg',
      // Footer logo (shows the moment the user leaves `/`).
      '/img/footer-logo.png',
      // Header logo lockup (shows on every interior route).
      '/img/logo.svg',
    ];
    for (const href of assets) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.appendChild(link);
    }
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    if (ready) prefetchFlowAssets();
  }, [ready, prefetchFlowAssets]);

  return (
    <div className="home">
      <video
        ref={bgRef}
        // Remount when orientation changes so the new BG <source>s are picked up.
        key={orientation}
        className="home__bg"
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      >
        <source src={`/video/home-bg-${orientation}.webm`} type="video/webm" />
        <source src={`/video/home-bg-${orientation}.mp4`} type="video/mp4" />
      </video>

      <div className="home__overlay" aria-hidden="true" />

      <div className={`home__content${ready ? ' home__content--ready' : ''}`}>
        {/* Animated logo lockup — alpha video that plays once and freezes on
            its last frame. WebM (VP9 alpha) for Chrome/Firefox, HEVC (.mov)
            for Safari, PNG of the final frame for any browser that can't
            play either. The wrapper crops the bottom alpha pad (the lowest
            animated pixel sits at row 925 of 1080) so the CTA can sit
            closer to the visible artwork — see `.home__logo-wrap`. */}
        <div className="home__logo-wrap">
          <video
            ref={logoRef}
            className="home__logo"
            muted
            playsInline
            preload="auto"
            poster="/img/home-logo-final.png"
            aria-label={t('home.logoAlt')}
          >
            {useHEVCAlpha ? (
              <source src="/video/home-logo.mov" type='video/mp4; codecs="hvc1"' />
            ) : (
              <source src="/video/home-logo.webm" type="video/webm" />
            )}
            <img src="/img/home-logo-final.png" alt={t('home.logoAlt')} />
          </video>
        </div>

        <div className="home__cta-group">
          <button
            type="button"
            className="home__cta"
            onClick={() => {
              prefetchFlowAssets();
              navigate('/id-card');
            }}
          >
            {t('home.enter')}
          </button>
          <button type="button" className="home__cta" onClick={() => navigate('/class')}>
            {QUIZ_COPY.getToClass}
          </button>
        </div>
      </div>

      <div
        className={`home__preloader${ready ? ' home__preloader--hidden' : ''}`}
        aria-hidden={ready}
      >
        <div ref={barRef} className="home__preloader-bar" />
      </div>
    </div>
  );
}
